import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tab, Nav, Row, Col } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ProgramDropdown } from "CommonDropdowns";
import { MainLoader } from "CommonComponent";
import {
  createInvoiceConfiguration,
  fetchInvoiceConfigurations,
  fetchBoundTasks,
  getInvoiceConfigurationById,
  toggleInvoiceConfigurationSections,
  updateInvoiceConfiguration,
  hideInvoiceConfigurationValidationError,
} from "actions";
import { validateInvoiceConfigurationForm } from "pages/InvoiceConfiguration/formasate/validate";
import { LaborRightContent, LaborTab } from "./component/Labor";
import { TechnologyRightContent, TechnologyTab } from "./component/Technology";
import FulfilmentTab from "./component/Fulfilment/FulfilmentTab";
import FulfilmentRightContent from "./component/Fulfilment/FulfilmentRightContent";
import { MiscExpensesTab, MiscRightContent } from "./component/MiscExpenses";
import "./UpdateInvoiceConfiguration.css";

const tabs = [
  { id: "labor", value: "Labor" },
  { id: "technology", value: "Technology" },
  { id: "fulfilment", value: "Fulfilment" },
  { id: "miscellaneous_expenses", value: "Miscellaneous Expenses" },
];

const TAB_SECTION_OPTIONS = {
  labor: [],
  technology: [
    "crm_subscription_annual",
    "telecom_support",
    "telecom_consumption",
    "misc_telecom",
  ],
  fulfilment: [
    "fulfillment",
    "fulfillment_misc",
    "fulfillment_tracker",
    "fulfillment_tracker_1",
    "supplies",
    "storage",
    "gift_cards",
    "fedex_expenses",
  ],
  miscellaneous_expenses: [
    "professional_voice_recording",
    "misc_telecom",
    "misc_credit",
  ],
};

const toLaborTaskSectionId = (taskId) => `task_${taskId}`;

const FIXED_LABOR_SECTION_OPTIONS = [
  {
    id: "rep_hours_onshore",
    label: "Rep Hours (Onshore)",
    type: "rep_rates",
  },
  {
    id: "rep_hours_offshore",
    label: "Rep Hours (Offshore)",
    type: "rep_rates",
  },
];

const toLaborSectionOptions = (payload) => {
  const records = extractRecords(payload);

  return records
    .map((item, index) => {
      const taskId =
        item?.task?.id ??
        item?.task_id ??
        item?.taskId ??
        item?.id ??
        index;
      const label =
        [
          item?.task?.category?.name ||
            item?.category_name ||
            item?.categoryName ||
            "",
          item?.task?.name ||
            item?.task_name ||
            item?.taskName ||
            `Task ${index + 1}`,
        ]
          .filter(Boolean)
          .join(" - ") ||
        item?.task_name ||
        item?.taskName ||
        `Task ${index + 1}`;

      if (taskId === undefined || taskId === null || taskId === "") return null;

      return {
        id: toLaborTaskSectionId(taskId),
        label,
        taskId,
      };
    })
    .filter(Boolean);
};

const SECTION_TO_TAB = Object.entries(TAB_SECTION_OPTIONS).reduce(
  (acc, [tabId, sectionIds]) => {
    sectionIds.forEach((sectionId) => {
      acc[sectionId] = tabId;
    });
    return acc;
  },
  { telecom: "technology" }
);

const defaultLaborSections = [];
const defaultTechnologySections = ["crm_subscription_annual"];
const defaultFulfilmentSections = ["fulfillment"];
const defaultMiscSections = ["professional_voice_recording"];

const createDefaultSectionData = () => ({
  labor: {},
  technology: {},
  fulfilment: {},
  miscellaneous_expenses: {},
});

const createAllSectionsSelected = (laborSectionIds = []) => ({
  labor: [...laborSectionIds],
  technology: [...TAB_SECTION_OPTIONS.technology],
  fulfilment: [...TAB_SECTION_OPTIONS.fulfilment],
  miscellaneous_expenses: [...TAB_SECTION_OPTIONS.miscellaneous_expenses],
});

const extractRecords = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (payload?.data && typeof payload.data === "object") return [payload.data];
  if (payload && typeof payload === "object") return [payload];
  return [];
};

const isEnabledValue = (rawEnabled) =>
  rawEnabled === true ||
  rawEnabled === 1 ||
  rawEnabled === "1" ||
  rawEnabled === "true";

const normalizeTabId = (tabId) => {
  const normalized = String(tabId || "").trim().toLowerCase();

  if (normalized === "fulfillment") return "fulfilment";
  if (normalized === "miscellaneous" || normalized === "misc") {
    return "miscellaneous_expenses";
  }

  return normalized;
};

const normalizeSectionStates = (
  sectionStates = [],
  sectionEnablementMap = null,
  tabsData = null
) => {
  const state = {
    labor: defaultLaborSections,
    technology: defaultTechnologySections,
    fulfilment: defaultFulfilmentSections,
    miscellaneous_expenses: defaultMiscSections,
  };

  const next = {
    labor: [],
    technology: [],
    fulfilment: [],
    miscellaneous_expenses: [],
  };

  if (Array.isArray(sectionStates) && sectionStates.length > 0) {
    sectionStates.forEach((item) => {
      const sectionId = item?.section_id || item?.sectionId;
      const inferredTab =
        SECTION_TO_TAB[sectionId] ||
        (String(sectionId || "").startsWith("task_") ||
        String(sectionId || "").startsWith("rep_hours_")
          ? "labor"
          : null);
      const tab = normalizeTabId(item?.tab || inferredTab);
      const rawEnabled = item?.is_enabled ?? item?.isEnabled;
      const isEnabled = isEnabledValue(rawEnabled);

      if (!tab || !sectionId || !isEnabled) return;

      const mappedIds =
        sectionId === "telecom"
          ? ["telecom_support", "telecom_consumption"]
          : [sectionId];

      next[tab] = [...next[tab], ...mappedIds];
    });
  }

  Object.keys(next).forEach((tabId) => {
    next[tabId] = Array.from(new Set(next[tabId]));
  });

  const hasNormalizedFromSectionStates =
    next.labor.length > 0 ||
    next.technology.length > 0 ||
    next.fulfilment.length > 0 ||
    next.miscellaneous_expenses.length > 0;

  if (hasNormalizedFromSectionStates) {
    return {
      labor: next.labor.length ? next.labor : defaultLaborSections,
      technology: next.technology.length
        ? next.technology
        : defaultTechnologySections,
      fulfilment: next.fulfilment.length
        ? next.fulfilment
        : defaultFulfilmentSections,
      miscellaneous_expenses: next.miscellaneous_expenses.length
        ? next.miscellaneous_expenses
        : defaultMiscSections,
    };
  }

  const nextFromMap = {
    labor: [],
    technology: [],
    fulfilment: [],
    miscellaneous_expenses: [],
  };

  let hasEnablementSource = false;

  Object.entries(TAB_SECTION_OPTIONS).forEach(([tabId, sectionIds]) => {
    const mapSections = sectionEnablementMap?.[tabId];
    const tabEnabledSections = tabsData?.[tabId]?.enabled_sections;
    const enabledSource =
      mapSections && typeof mapSections === "object"
        ? mapSections
        : tabEnabledSections && typeof tabEnabledSections === "object"
          ? tabEnabledSections
          : null;

    if (!enabledSource) return;
    hasEnablementSource = true;

    const candidateSectionIds =
      tabId === "labor"
        ? Object.keys(enabledSource)
        : sectionIds;

    candidateSectionIds.forEach((sectionId) => {
      if (isEnabledValue(enabledSource?.[sectionId])) {
        nextFromMap[tabId] = [...nextFromMap[tabId], sectionId];
      }
    });

    if (
      tabId === "technology" &&
      isEnabledValue(enabledSource?.telecom) &&
      !nextFromMap[tabId].includes("telecom_support")
    ) {
      nextFromMap[tabId] = [
        ...nextFromMap[tabId],
        "telecom_support",
        "telecom_consumption",
      ];
    }
  });

  if (hasEnablementSource) {
    return nextFromMap;
  }

  return {
    labor: next.labor.length ? next.labor : defaultLaborSections,
    technology: next.technology.length ? next.technology : defaultTechnologySections,
    fulfilment: next.fulfilment.length ? next.fulfilment : defaultFulfilmentSections,
    miscellaneous_expenses: next.miscellaneous_expenses.length
      ? next.miscellaneous_expenses
      : defaultMiscSections,
  };
};

const ARRAY_SECTION_IDS = new Set([
  "crm_subscription_annual",
  "misc_telecom",
  "misc_credit",
  "fulfillment_misc",
  "supplies",
  "storage",
  "gift_cards",
  "fedex_expenses",
]);

const normalizeLineItems = (lineItems = [], tabsData = null) => {
  const next = createDefaultSectionData();
  const hasPayloadValue = (value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === "object") return Object.keys(value).length > 0;
    return value !== undefined && value !== null && value !== "";
  };

  if (Array.isArray(lineItems) && lineItems.length > 0) {
    lineItems.forEach((item) => {
      const rawSectionId = item?.section_id || item?.sectionId;
      const tab = normalizeTabId(item?.tab || SECTION_TO_TAB[rawSectionId]);

      if (!tab || !rawSectionId) return;

      const lineItemPayload =
        item?.payload ??
        item?.value ??
        item?.data ??
        item?.line_item_value ??
        item?.content ??
        {};

      const sectionPayloadFromTabs =
        tabsData?.[tab]?.sections?.[rawSectionId] ??
        (rawSectionId === "telecom"
          ? tabsData?.[tab]?.sections?.telecom
          : undefined);
      const shouldMergePayloads =
        lineItemPayload &&
        typeof lineItemPayload === "object" &&
        !Array.isArray(lineItemPayload) &&
        sectionPayloadFromTabs &&
        typeof sectionPayloadFromTabs === "object" &&
        !Array.isArray(sectionPayloadFromTabs);

      const mergedLineItemPayload = shouldMergePayloads
        ? {
            ...sectionPayloadFromTabs,
            ...lineItemPayload,
          }
        : lineItemPayload;

      const resolvedPayload = hasPayloadValue(mergedLineItemPayload)
        ? mergedLineItemPayload
        : hasPayloadValue(sectionPayloadFromTabs)
          ? sectionPayloadFromTabs
          : {};

      if (rawSectionId === "telecom") {
        const legacy =
          resolvedPayload && typeof resolvedPayload === "object"
            ? resolvedPayload
            : {};

        next[tab].telecom_support = {
          title: legacy.title,
          licenseRate: legacy.licenseRate,
          numberOfLicenses:
            legacy.numberOfLicenses ??
            legacy.noOfLicenses ??
            legacy.noOfLicense,
        };
        next[tab].telecom_consumption = {
          rate: legacy.rate ?? legacy.perMinuteRate,
          chargeTimeType: legacy.chargeTimeType || legacy.charge_time_type,
          chargeTimeUnit: legacy.chargeTimeUnit || legacy.charge_time_unit,
        };
        return;
      }

      if (ARRAY_SECTION_IDS.has(rawSectionId)) {
        if (
          Array.isArray(sectionPayloadFromTabs) &&
          sectionPayloadFromTabs.length > 0
        ) {
          if (next[tab][rawSectionId] === undefined) {
            next[tab][rawSectionId] = sectionPayloadFromTabs;
          }
          return;
        }

        const rowPayload =
          resolvedPayload &&
          typeof resolvedPayload === "object" &&
          !Array.isArray(resolvedPayload)
            ? resolvedPayload
            : {};

        if (!Array.isArray(next[tab][rawSectionId])) {
          next[tab][rawSectionId] = [];
        }
        next[tab][rawSectionId].push(rowPayload);
        return;
      }

      next[tab][rawSectionId] = resolvedPayload;
    });

    return next;
  }

  Object.entries(TAB_SECTION_OPTIONS).forEach(([tabId, sectionIds]) => {
    const sections = tabsData?.[tabId]?.sections;
    if (!sections || typeof sections !== "object") return;

    const candidateSectionIds =
      tabId === "labor" ? Object.keys(sections) : sectionIds;

    candidateSectionIds.forEach((sectionId) => {
      if (sections?.[sectionId] === undefined) return;

      if (ARRAY_SECTION_IDS.has(sectionId)) {
        const value = sections[sectionId];
        next[tabId][sectionId] = Array.isArray(value)
          ? value
          : value && typeof value === "object"
            ? [value]
            : [];
        return;
      }

      next[tabId][sectionId] = sections[sectionId];
    });

    if (tabId === "technology" && sections?.telecom) {
      const legacy =
        sections.telecom && typeof sections.telecom === "object"
          ? sections.telecom
          : {};

      if (next[tabId].telecom_support === undefined) {
        next[tabId].telecom_support = {
          title: legacy.title,
          licenseRate: legacy.licenseRate,
          numberOfLicenses:
            legacy.numberOfLicenses ??
            legacy.noOfLicenses ??
            legacy.noOfLicense,
        };
      }

      if (next[tabId].telecom_consumption === undefined) {
        next[tabId].telecom_consumption = {
          rate: legacy.rate ?? legacy.perMinuteRate,
          chargeTimeType: legacy.chargeTimeType || legacy.charge_time_type,
          chargeTimeUnit: legacy.chargeTimeUnit || legacy.charge_time_unit,
        };
      }
    }
  });

  return next;
};

const unwrapRecord = (record) => {
  if (!record) return null;
  if (record?.data && typeof record.data === "object" && !Array.isArray(record.data)) {
    return record.data;
  }
  return record;
};

const normalizeProgramId = (value) => {
  if (value === undefined || value === null || value === "") return "";
  return String(value);
};

const toProgramSelectedChangingId = (programId) => {
  const normalized = normalizeProgramId(programId);
  if (!normalized) return "";
  return /^\d+$/.test(normalized) ? Number(normalized) : normalized;
};

const getFieldErrorText = (errors, field) => {
  const errorValue = errors?.[field];
  if (Array.isArray(errorValue)) return errorValue[0] || "";
  if (typeof errorValue === "string") return errorValue;
  return "";
};

const DECIMAL_FIELDS = new Set([
  "flatRate",
  "includedHours",
  "hourlyRate",
  "flatRateHours",
  "weekdayRate",
  "weekendRate",
  "socialMediaWeekdayRate",
  "socialMediaWeekendRate",
  "overtimeHourRate",
  "holidayHourRate",
  "crmSubscription",
  "socialCrmSubscription",
  "licenseRate",
  "perMinuteRate",
  "rate",
  "charge",
  "perPieceRate",
  "postageRate",
  "handlingRate",
  "checkProcessingRate",
  "voidProcessingRate",
  "stoppedPaymentRate",
  "retrievalHoursRate",
  "retrievalRate",
  "nonRetrievalRate",
  "minutesPerRetrieval",
  "markupRate",
  "defaultCharge",
  "processingFee",
  "baseCharge",
  "quoteAmount",
]);

const sanitizeDecimal = (value) => {
  if (value === undefined || value === null || value === "") return "";
  return String(value).replace(/[^0-9.-]/g, "");
};

const CRM_OMIT_FIELDS = new Set([
  "socialCrmTitle",
  "socialCrmSubscription",
  "socialCrmDateFrom",
  "socialCrmDateTo",
  "isLocked",
  "canEdit",
  "canDelete",
  "lockReason",
]);

const sanitizePayloadBySchema = (payload = {}) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return payload;

  const sanitized = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (CRM_OMIT_FIELDS.has(key)) return;

    if (key === "isPaid") {
      sanitized[key] =
        value === true || value === 1 || value === "1" || value === "true";
      return;
    }

    if (key === "perPieceRateByBrand" && value && typeof value === "object" && !Array.isArray(value)) {
      sanitized[key] = Object.entries(value).reduce((acc, [brand, rate]) => {
        const normalizedBrand = String(brand || "").trim();
        if (!normalizedBrand) return acc;
        acc[normalizedBrand] = sanitizeDecimal(rate);
        return acc;
      }, {});
      return;
    }

    if (
      (key === "pieceTitleByBrand" || key === "postageTitleByBrand") &&
      value &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      sanitized[key] = Object.entries(value).reduce((acc, [brand, text]) => {
        const normalizedBrand = String(brand || "").trim();
        if (!normalizedBrand) return acc;
        acc[normalizedBrand] = text === undefined || text === null ? "" : String(text);
        return acc;
      }, {});
      return;
    }

    sanitized[key] = DECIMAL_FIELDS.has(key) ? sanitizeDecimal(value) : value;
  });

  return sanitized;
};

const createLaborTaskPayloadDefaults = (title = "") => ({
  title,
  isFlatRate: true,
  flatRate: "",
  includedHours: "",
  hourlyRate: "",
  chargeOverageHours: false,
});

const createLaborRepRatePayloadDefaults = () => ({
  weekdayRateTitle: "Rep Hours Weekdays",
  weekdayNumberOfFTEs: "",
  weekdayHoursPerDay: "",
  weekdayIncludeInInvoice: true,
  weekdayRate: "",
  weekendRateTitle: "Rep Hours Weekends",
  weekendNumberOfFTEs: "",
  weekendHoursPerDay: "",
  weekendIncludeInInvoice: true,
  weekendRate: "",
  socialMediaWeekdayRateTitle: "Social Media Weekdays",
  socialMediaWeekdayNumberOfFTEs: "",
  socialMediaWeekdayHoursPerDay: "",
  socialMediaWeekdayIncludeInInvoice: true,
  socialMediaWeekdayRate: "",
  socialMediaWeekendRateTitle: "Social Media Weekends",
  socialMediaWeekendNumberOfFTEs: "",
  socialMediaWeekendHoursPerDay: "",
  socialMediaWeekendIncludeInInvoice: true,
  socialMediaWeekendRate: "",
  overtimeHourRateTitle: "Overtime Charge",
  overtimeIncludeInInvoice: true,
  overtimeHourRate: "",
  holidayHourRateTitle: "Holiday Charge",
  holidayIncludeInInvoice: true,
  holidayHourRate: "",
});

const isEmptyPlainObject = (value) =>
  value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.keys(value).length === 0;

const UpdateInvoiceConfiguration = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const session = useSelector((state) => state.session);
  const invoiceConfigurationState = useSelector(
    (state) => state.invoiceConfigurationState
  );
  const object_viewed_id = session?.current_page_permissions?.object_id || "";

  const [activeKey, setActiveKey] = useState("labor");
  const [configId, setConfigId] = useState(null);
  const [programId, setProgramId] = useState("");
  const [programValue, setProgramValue] = useState(null);
  const [initialSeed, setInitialSeed] = useState(0);
  const [laborSectionOptions, setLaborSectionOptions] = useState([]);

  const mergedLaborSectionOptions = useMemo(
    () => [...laborSectionOptions, ...FIXED_LABOR_SECTION_OPTIONS],
    [laborSectionOptions]
  );

  const laborSectionLabelMap = useMemo(
    () =>
      mergedLaborSectionOptions.reduce((acc, option) => {
        acc[option.id] = option.label;
        return acc;
      }, {}),
    [mergedLaborSectionOptions]
  );

  const laborSectionIds = useMemo(
    () => mergedLaborSectionOptions.map((option) => option.id),
    [mergedLaborSectionOptions]
  );

  const [selectedLaborSections, setSelectedLaborSections] = useState(defaultLaborSections);
  const [selectedTechnologySections, setSelectedTechnologySections] = useState(
    defaultTechnologySections
  );
  const [selectedFulfilmentSections, setSelectedFulfilmentSections] = useState(
    defaultFulfilmentSections
  );
  const [selectedMiscSections, setSelectedMiscSections] = useState(defaultMiscSections);

  const [sectionData, setSectionData] = useState(createDefaultSectionData);
  const [formErrors, setFormErrors] = useState({});
  const pendingUserFieldEditRef = useRef(false);

  const resetFormForProgram = useCallback(() => {
    const allSelected = createAllSectionsSelected(laborSectionIds);
    setConfigId(null);
    setSelectedLaborSections(allSelected.labor);
    setSelectedTechnologySections(allSelected.technology);
    setSelectedFulfilmentSections(allSelected.fulfilment);
    setSelectedMiscSections(allSelected.miscellaneous_expenses);
    setSectionData(createDefaultSectionData());
    setInitialSeed((prev) => prev + 1);
  }, [laborSectionIds]);

  const applyRecordToForm = useCallback((record) => {
    const resolvedRecord = unwrapRecord(record);
    if (!resolvedRecord) return;

    const normalizedSections = normalizeSectionStates(
      resolvedRecord?.section_states || resolvedRecord?.sectionStates || [],
      resolvedRecord?.section_enablement_map || null,
      resolvedRecord?.tabs || null
    );
    const normalizedLineItems = normalizeLineItems(
      resolvedRecord?.line_items || resolvedRecord?.lineItems || [],
      resolvedRecord?.tabs || null
    );

    setSelectedLaborSections(
      normalizedSections.labor.length > 0
        ? normalizedSections.labor
        : laborSectionIds
    );
    setSelectedTechnologySections(normalizedSections.technology);
    setSelectedFulfilmentSections(normalizedSections.fulfilment);
    setSelectedMiscSections(normalizedSections.miscellaneous_expenses);
    setSectionData(normalizedLineItems);

    const detectedProgramId =
      resolvedRecord?.program_id ||
      resolvedRecord?.program?.id ||
      resolvedRecord?.programId ||
      "";
    const normalizedDetectedProgramId = normalizeProgramId(detectedProgramId);
    setProgramId(normalizedDetectedProgramId);

    if (normalizedDetectedProgramId) {
      const resolvedProgramLabel =
        resolvedRecord?.program_name || resolvedRecord?.program?.name || "";

      setProgramValue((prev) => {
        if (resolvedProgramLabel) {
          return {
            value: normalizedDetectedProgramId,
            label: resolvedProgramLabel,
          };
        }

        if (String(prev?.value) === normalizedDetectedProgramId) {
          return prev;
        }

        return null;
      });
    }

    setInitialSeed((prev) => prev + 1);
  }, [laborSectionIds]);

  useEffect(() => {
    if (!programId) {
      setLaborSectionOptions([]);
      return;
    }

    dispatch(fetchBoundTasks(programId, { object_viewed_id }))
      .then((res) => {
        setLaborSectionOptions(toLaborSectionOptions(res?.data));
      })
      .catch(() => {
        setLaborSectionOptions([]);
      });
  }, [dispatch, object_viewed_id, programId]);

  useEffect(() => {
    if (!programId) return;

    setSelectedLaborSections((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const next = laborSectionIds.filter((id) => current.includes(id));

      if (next.length > 0) return next;
      return [...laborSectionIds];
    });
  }, [laborSectionIds, programId]);

  const loadConfigurationByProgramId = useCallback(
    (selectedProgramId) => {
      if (!selectedProgramId) {
        resetFormForProgram();
        return;
      }

      dispatch(
        fetchInvoiceConfigurations(
          {
            program_id: selectedProgramId,
            per_page: 1,
            page: 1,
          },
          object_viewed_id
        )
      ).then((res) => {
        const records = extractRecords(res?.data);
        if (records.length > 0) {
          applyRecordToForm(records[0]);
        } else {
          resetFormForProgram();
        }
      }).catch(() => {
        // Action handles snackbar/validation; avoid unhandled rejection runtime error.
      });
    },
    [dispatch, object_viewed_id, applyRecordToForm, resetFormForProgram]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryId = params.get("id");
    const queryProgramId = params.get("program_id");

    if (queryProgramId) {
      const normalizedQueryProgramId = normalizeProgramId(queryProgramId);
      setProgramId(normalizedQueryProgramId);
      setProgramValue((prev) =>
        String(prev?.value) === normalizedQueryProgramId
          ? prev
          : null
      );

      if (!queryId) {
        loadConfigurationByProgramId(normalizedQueryProgramId);
      }
    }

    if (queryId) {
      setConfigId(queryId);
      dispatch(getInvoiceConfigurationById(queryId, "update", object_viewed_id));
      return;
    }

    setConfigId(null);
  }, [dispatch, location.search, object_viewed_id, loadConfigurationByProgramId]);

  useEffect(() => {
    const record = unwrapRecord(invoiceConfigurationState?.invoiceConfigurationRecord);
    if (!record) return;

    const recordId = record?.id || record?.config_id;
    if (recordId) {
      setConfigId(String(recordId));
    }

    applyRecordToForm(record);
  }, [invoiceConfigurationState?.invoiceConfigurationRecord, applyRecordToForm]);

  useEffect(() => {
    const validationErrors = invoiceConfigurationState?.validation_error;
    if (!validationErrors || typeof validationErrors !== "object") return;

    setFormErrors((prev) => ({
      ...prev,
      ...validationErrors,
    }));
  }, [invoiceConfigurationState?.validation_error]);

  const handleProgramChange = (selected) => {
    setProgramValue(selected || null);
    const selectedProgramId = normalizeProgramId(selected?.value);
    setProgramId(selectedProgramId);
    setFormErrors({});
    dispatch(hideInvoiceConfigurationValidationError("program_id"));

    if (!selectedProgramId) {
      resetFormForProgram();
      return;
    }

    loadConfigurationByProgramId(selectedProgramId);
  };

  const onTabSectionDataChange = useCallback((tabId, data) => {
    setSectionData((prev) => ({
      ...prev,
      [tabId]: data || {},
    }));

    if (pendingUserFieldEditRef.current) {
      setFormErrors((prev) => {
        const next = { ...(prev || {}) };
        Object.keys(next).forEach((key) => {
          if (String(key).startsWith("line_items.")) {
            delete next[key];
          }
        });
        return next;
      });

      pendingUserFieldEditRef.current = false;
    }
  }, []);

  const handleInputChangeCapture = useCallback((event) => {
    const tagName = String(event?.target?.tagName || "").toLowerCase();
    if (tagName === "input" || tagName === "textarea" || tagName === "select") {
      pendingUserFieldEditRef.current = true;
    }
  }, []);

  const sectionStatesPayload = useMemo(() => {
    const toRows = (tab, selected, all) =>
      all.map((sectionId) => ({
        tab,
        section_id: sectionId,
        is_enabled: selected.includes(sectionId),
      }));

    return [
      ...toRows("labor", selectedLaborSections, laborSectionIds),
      ...toRows("technology", selectedTechnologySections, TAB_SECTION_OPTIONS.technology),
      ...toRows("fulfilment", selectedFulfilmentSections, TAB_SECTION_OPTIONS.fulfilment),
      ...toRows(
        "miscellaneous_expenses",
        selectedMiscSections,
        TAB_SECTION_OPTIONS.miscellaneous_expenses
      ),
    ];
  }, [
    laborSectionIds,
    selectedLaborSections,
    selectedTechnologySections,
    selectedFulfilmentSections,
    selectedMiscSections,
  ]);

  const lineItemsWithMeta = useMemo(() => {
    const items = [];

    const resolveLaborFallbackPayload = (sectionId) => {
      if (sectionId === "rep_hours_onshore" || sectionId === "rep_hours_offshore") {
        return createLaborRepRatePayloadDefaults();
      }

      return createLaborTaskPayloadDefaults(
        laborSectionLabelMap?.[sectionId] ||
          String(sectionId || "")
            .replace(/^task_/, "")
            .replace(/_/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase())
      );
    };

    const appendFromTab = (tab, selectedSections) => {
      const tabData = sectionData?.[tab] || {};

      selectedSections.forEach((sectionId) => {
        const payload = tabData?.[sectionId];

        if (Array.isArray(payload)) {
          payload.forEach((row, rowIndex) => {
            items.push({
              tab,
              section_id: sectionId,
              payload: sanitizePayloadBySchema(row || {}),
              __meta: { section_id: sectionId, rowIndex },
            });
          });
          return;
        }

        const resolvedPayload =
          tab === "labor" && (payload === undefined || payload === null || isEmptyPlainObject(payload))
            ? resolveLaborFallbackPayload(sectionId)
            : payload;

        items.push({
          tab,
          section_id: sectionId,
          payload: sanitizePayloadBySchema(resolvedPayload !== undefined ? resolvedPayload : {}),
          __meta: { section_id: sectionId, rowIndex: null },
        });
      });
    };

    appendFromTab("labor", selectedLaborSections);
    appendFromTab("technology", selectedTechnologySections);
    appendFromTab("fulfilment", selectedFulfilmentSections);
    appendFromTab("miscellaneous_expenses", selectedMiscSections);

    return items;
  }, [
    laborSectionLabelMap,
    sectionData,
    selectedLaborSections,
    selectedTechnologySections,
    selectedFulfilmentSections,
    selectedMiscSections,
  ]);

  const lineItemsPayload = useMemo(
    () =>
      lineItemsWithMeta.map(({ __meta, ...lineItem }) => lineItem),
    [lineItemsWithMeta]
  );

  const getSectionFieldError = useCallback(
    (sectionId, fieldName, rowIndex = null) => {
      if (!formErrors || !sectionId || !fieldName) return "";

      const oneBasedIndex = rowIndex === null ? null : rowIndex + 1;
      const directCandidates = [
        `${sectionId}.${fieldName}`,
        `${sectionId}.payload.${fieldName}`,
        `sections.${sectionId}.${fieldName}`,
        ...(rowIndex === null
          ? []
          : [
              `${sectionId}.${rowIndex}.${fieldName}`,
              `${sectionId}.${oneBasedIndex}.${fieldName}`,
              `${sectionId}[${rowIndex}].${fieldName}`,
              `${sectionId}[${oneBasedIndex}].${fieldName}`,
              `technology.${sectionId}.${rowIndex}.${fieldName}`,
              `technology.${sectionId}.${oneBasedIndex}.${fieldName}`,
              `technology.${sectionId}[${rowIndex}].${fieldName}`,
              `technology.${sectionId}[${oneBasedIndex}].${fieldName}`,
              `fulfilment.${sectionId}.${rowIndex}.${fieldName}`,
              `fulfilment.${sectionId}.${oneBasedIndex}.${fieldName}`,
              `fulfilment.${sectionId}[${rowIndex}].${fieldName}`,
              `fulfilment.${sectionId}[${oneBasedIndex}].${fieldName}`,
            ]),
      ];

      for (const candidate of directCandidates) {
        const direct = getFieldErrorText(formErrors, candidate);
        if (direct) return direct;
      }

      for (const [key, value] of Object.entries(formErrors)) {
        const match = /^line_items\.(\d+)\.payload(?:\.(.+))?$/.exec(String(key));
        if (!match) continue;

        const lineItemIndex = Number(match[1]);
        const backendField = match[2] || "";
        const meta = lineItemsWithMeta[lineItemIndex]?.__meta;

        if (!meta || meta.section_id !== sectionId) continue;
        if (backendField && backendField !== fieldName) continue;
        if (rowIndex !== null && meta.rowIndex !== rowIndex) continue;

        if (Array.isArray(value)) return value[0] || "";
        if (typeof value === "string") return value;
      }

      return "";
    },
    [formErrors, lineItemsWithMeta]
  );

  const tabErrorsById = useMemo(() => {
    const next = {
      labor: false,
      technology: false,
      fulfilment: false,
      miscellaneous_expenses: false,
    };

    Object.entries(formErrors || {}).forEach(([key, value]) => {
      const hasError = Array.isArray(value) ? value.length > 0 : Boolean(value);
      if (!hasError) return;

      const errorKey = String(key || "");
      if (!errorKey || errorKey === "program_id" || errorKey === "section_states") {
        return;
      }

      const lineItemMatch = /^line_items\.(\d+)\./.exec(errorKey);
      if (lineItemMatch) {
        const lineItemIndex = Number(lineItemMatch[1]);
        const sectionId = lineItemsWithMeta?.[lineItemIndex]?.__meta?.section_id;
        const tabId = laborSectionIds.includes(sectionId)
          ? "labor"
          : SECTION_TO_TAB[sectionId];
        if (tabId) next[tabId] = true;
        return;
      }

      const sectionIdCandidate = errorKey.split(".")[0];
      const tabId = laborSectionIds.includes(sectionIdCandidate)
        ? "labor"
        : SECTION_TO_TAB[sectionIdCandidate];
      if (tabId) {
        next[tabId] = true;
      }
    });

    return next;
  }, [formErrors, laborSectionIds, lineItemsWithMeta]);

  const runTogglePatchIfNeeded = (tab, sectionId, isEnabled) => {
    if (!configId) return;

    dispatch(
      toggleInvoiceConfigurationSections(
        configId,
        [{ tab, section_id: sectionId, is_enabled: isEnabled }],
        object_viewed_id
      )
    ).catch(() => {
      // Action handles snackbar/validation; avoid unhandled rejection runtime error.
    });
  };

  const createToggleHandler = (tab, setter) => (sectionId) => {
    setFormErrors((prev) => ({
      ...prev,
      section_states: undefined,
    }));

    setter((prev) => {
      const exists = prev.includes(sectionId);
      const next = exists ? prev.filter((id) => id !== sectionId) : [...prev, sectionId];
      runTogglePatchIfNeeded(tab, sectionId, !exists);
      return next;
    });
  };

  const handleLaborSectionToggle = createToggleHandler("labor", setSelectedLaborSections);
  const handleTechnologySectionToggle = createToggleHandler(
    "technology",
    setSelectedTechnologySections
  );
  const handleFulfilmentSectionToggle = createToggleHandler(
    "fulfilment",
    setSelectedFulfilmentSections
  );
  const handleMiscSectionToggle = createToggleHandler(
    "miscellaneous_expenses",
    setSelectedMiscSections
  );

  const handleSave = () => {
    const values = {
      program_id: programId,
      section_states: sectionStatesPayload.filter((s) => s.is_enabled),
    };
    const errors = validateInvoiceConfigurationForm(values);

    if (errors) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});

    const payload = {
      program_id: programId,
      section_states: sectionStatesPayload,
      line_items: lineItemsPayload,
    };

    const action = configId
      ? updateInvoiceConfiguration(configId, payload, object_viewed_id)
      : createInvoiceConfiguration(payload, object_viewed_id);

    dispatch(action).then((res) => {
      const returnedId = res?.data?.id || res?.data?.config_id;
      if (returnedId) {
        setConfigId(String(returnedId));
      }
    }).catch(() => {
      // Action handles snackbar/validation; avoid unhandled rejection runtime error.
    });
  };

  const handleLaborDataChange = useCallback(
    (data) => onTabSectionDataChange("labor", data),
    [onTabSectionDataChange]
  );

  const handleTechnologyDataChange = useCallback(
    (data) => onTabSectionDataChange("technology", data),
    [onTabSectionDataChange]
  );

  const handleFulfilmentDataChange = useCallback(
    (data) => onTabSectionDataChange("fulfilment", data),
    [onTabSectionDataChange]
  );

  const handleMiscDataChange = useCallback(
    (data) => onTabSectionDataChange("miscellaneous_expenses", data),
    [onTabSectionDataChange]
  );

  const tabComponents = {
    labor: (
      <LaborTab
        key={`labor-${initialSeed}`}
        selectedSections={selectedLaborSections}
        sectionOptions={mergedLaborSectionOptions}
        initialSectionData={sectionData.labor}
        getFieldError={getSectionFieldError}
        onSectionDataChange={handleLaborDataChange}
      />
    ),
    technology: (
      <TechnologyTab
        key={`technology-${initialSeed}`}
        selectedSections={selectedTechnologySections}
        initialSectionData={sectionData.technology}
        getFieldError={getSectionFieldError}
        onSectionDataChange={handleTechnologyDataChange}
      />
    ),
    fulfilment: (
      <FulfilmentTab
        key={`fulfilment-${initialSeed}`}
        selectedSections={selectedFulfilmentSections}
        initialSectionData={sectionData.fulfilment}
        getFieldError={getSectionFieldError}
        onSectionDataChange={handleFulfilmentDataChange}
      />
    ),
    miscellaneous_expenses: (
      <MiscExpensesTab
        key={`misc-${initialSeed}`}
        selectedSections={selectedMiscSections}
        initialSectionData={sectionData.miscellaneous_expenses}
        getFieldError={getSectionFieldError}
        onSectionDataChange={handleMiscDataChange}
      />
    ),
  };

  const tabRightComponents = {
    labor: (
      <LaborRightContent
        selectedSections={selectedLaborSections}
        sectionOptions={mergedLaborSectionOptions}
        onToggleSection={handleLaborSectionToggle}
      />
    ),
    technology: (
      <TechnologyRightContent
        selectedSections={selectedTechnologySections}
        onToggleSection={handleTechnologySectionToggle}
      />
    ),
    fulfilment: (
      <FulfilmentRightContent
        selectedSections={selectedFulfilmentSections}
        onToggleSection={handleFulfilmentSectionToggle}
      />
    ),
    miscellaneous_expenses: (
      <MiscRightContent
        selectedSections={selectedMiscSections}
        onToggleSection={handleMiscSectionToggle}
      />
    ),
  };

  return (
    <div className="wrapper-div" onChangeCapture={handleInputChangeCapture}>
      {invoiceConfigurationState?.loading && <MainLoader />}
      <div className="d-flex align-items-center justify-content-between px-3 pt-3">
        <h3 className="font-24 font-weight-bold heading-color d-flex align-items-center gap-2 m-0">
          <Link to="/invoiceConfiguration">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9.57 5.92969L3.5 11.9997L9.57 18.0697" stroke="#292D32" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M20.4999 12H3.66992" stroke="#292D32" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          Update Invoice Configuration
        </h3>

        {programId ? (
          <button
            type="button"
            className="n-u-btn"
            onClick={handleSave}
          >
            Save Configuration
          </button>
        ) : null}
      </div>

      <div className="p-3">
        <div>
          <Row>
            <Col lg={4}>
              <ProgramDropdown
                isMulti={false}
                name="program_id"
                selectedChangingId={toProgramSelectedChangingId(programId)}
                programValue={programValue}
                setProgramValue={setProgramValue}
                programOnChange={handleProgramChange}
                error={getFieldErrorText(formErrors, "program_id")}
              />
            </Col>
            <Col lg={8} className="d-flex align-items-center">
              {getFieldErrorText(formErrors, "section_states") && (
                <div className="text-danger font-12 mt-3">
                  {getFieldErrorText(formErrors, "section_states")}
                </div>
              )}
            </Col>
          </Row>
        </div>

        {programId ? (
          <div className="view-container">
            <Tab.Container activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
              <div className="p-3 border-bottom">
                <Nav variant="tabs" activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
                  {tabs.map((tab) => (
                    <Nav.Item key={tab.id}>
                      <Nav.Link
                        eventKey={tab.id}
                        className={tabErrorsById[tab.id] ? "text-danger" : ""}
                      >
                        {tab.value}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </div>

              <Tab.Content>
                {tabs.map((tab) => (
                  <Tab.Pane key={tab.id} eventKey={tab.id}>
                    <Row>
                      <Col lg={9}>
                        <div className="invoice-tabs-content border-end">
                          {tabComponents[tab.id] || "Tab"}
                        </div>
                      </Col>
                      <Col lg={3}>
                        <div className="invoice-tabs-content">
                          {tabRightComponents[tab.id] || null}
                        </div>
                      </Col>
                    </Row>
                  </Tab.Pane>
                ))}
              </Tab.Content>
            </Tab.Container>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default UpdateInvoiceConfiguration;